import React, { useState } from "react";
import { useLocation } from 'react-router';
import * as CaptchaLib from "@repoflow/browser/captcha";
import { useCaptcha } from "@repoflow/components/captcha";

import AuthSchema from "@common/schema/auth";
import * as Loading from "@repoflow/components/loading";

import { Component as Form } from "@repoflow/form-schema";
import { Container, Col, Row, Alert } from "reactstrap";
import * as Section from "@repoflow/components/section";
import { gql } from "@apollo/client";
import { basename } from "env";

const REGISTER = gql`
  mutation Attach(
    $password: String!
    $validation: String!
    $token: String!
  ) {
    viewer {
      anonymous(token: $token) {
        auth {
          attach(validation: $validation, password: $password) {
            id
            domain
            orgId
            domainId
          }
        }
      }
    }
  }
`;






// The link is the credential, exactly as on reset. Landing here without one is
// not an error state to recover from in place — there is nothing to fill in,
// and a bare password form with no token behind it is the very thing this page
// exists not to be.
const MissingToken = () => (
  <Container className="mt-4">
    <Row>
      <Col>
        <Alert color="danger">
          This invitation link is incomplete. Please use the link from your
          email, or ask for a new invitation.
        </Alert>
      </Col>
    </Row>
  </Container>
);

export default function () {
  const location = useLocation();
  const validation = new URLSearchParams(location.search).get("token");
  const domain = new URLSearchParams(location.search).get("domain");
  const [phase, setPhase] = useState("init");
  const { publicKey, ready } = useCaptcha();

  if (!validation) {
    return <MissingToken />;
  }

  if (!ready) {
    return <Loading.Content />;
  }

  return (
    <Section.Provider schema={AuthSchema}>
      <Section.Mutation
        guard={false}
        context={{ validation, domain, phase }}
        initial={{ password: "", confirm: "" }}
        mutation={REGISTER}
        schema={AuthSchema}
        target={{ $ref: "/Input/Attach/UI" }}
        onMutate={({ handler, state: { values } }) => {
          const { password, confirm } = values;

          // Belt and braces — the submit button is already disabled on a
          // mismatch. A typo here is recoverable via /auth/forgot, but only
          // after a round trip through email, so it is still worth catching.
          if (password !== confirm) {
            return;
          }

          setPhase("init");
          CaptchaLib.request(publicKey, (token) => {
            handler({
              variables: {
                password,
                validation,
                token,
              },
            });
          });
        }}
        onError={(error) => {
          setPhase("error");
        }}
        onCompleted={({
          viewer: {
            anonymous: {
              auth: { attach },
            },
          },
        }) => {
          if (attach) {
            const { orgId, domainId } = attach
            window.location = `${basename}/auth/login?domainId=${encodeURIComponent(domainId)}&orgId=${encodeURIComponent(orgId)}`;
          }
        }}
      >
        {MutationHandler}
      </Section.Mutation>
    </Section.Provider>
  );
};

const MutationHandler = ({
  schema,
  target,
  state: { values, isValid, setValues },
  error,
  context: { domain, phase }
}) => {
  const formProps = {
    schema,
    target,
    values,
    onChange: (values) => {
      setValues(values);
    },
    config: {
      "/Input/Attach/UI": {
        fields: [
          {
            label: "Password",
            property: "password",
            password: true,
          },
          {
            label: "Confirm",
            property: "confirm",
            password: true,
          },
        ],
      },
    },
  };

  // JSON Schema cannot express "this field must equal that one" without the
  // $data extension, which the jsonschema validator behind @repoflow/schema
  // does not implement — so the match is checked here and fed to the button.
  const { password, confirm } = values || {};
  const mismatch = Boolean(password) && Boolean(confirm) && password !== confirm;

  return (
    <>
      <Container>
        <Row>
          <Col className="text-center">
            <div className="mt-4"></div>
            {/* A heading, as on reset. The alert below says the same thing at
                length, but what the page is for has to be legible before
                anybody reads a paragraph. */}
            <h1>Set your password</h1>
          </Col>
        </Row>

        <Row>
          <Col className="text-center">
            <p>Your account has been successfully validated.</p>
            <p>Continue to <b>{domain}</b></p>
          </Col>
        </Row>

        <Row>
          <Col>
            {/* Reached only from a validation link, so the account has never
                been signed into and carries no password. Say so, rather than
                leaving a bare password form that reads like a sign in. */}
            <Alert color="info">
              This is your first time signing in, so your account does not have
              a password yet. Set a password for your account to continue.
            </Alert>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="mt-4"></div>
            <Form {...formProps} >
              <Section.Button type={"submit"} disabled={mismatch}>
                Set password
              </Section.Button>
            </Form>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            {mismatch && (
              <Alert color="warning">
                The two passwords do not match.
              </Alert>
            )}
            {phase === "error" && (
              <Alert color="danger">
                We could not set your password. The link may have expired —
                please request a new one or contact support.
              </Alert>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};